import CustomSnackBar from "@components/CustomSnackBar";
import UserForm from "@components/UserForm";
import { ROUTING_ADMIN_USERS } from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState,
  setSuccessAlert
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Paper, SelectChangeEvent, Typography } from "@mui/material";
import { usePatchUser, useUser } from "@services/api";
import { PatchUser, T_BOOL_FIELD, UserCreate } from "@services/api/types";
import { useQueryClient } from "@tanstack/react-query";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const DEFAULT_USER: UserCreate = {
  email: "",
  username: "",
  first_name: "",
  last_name: "",
  enabled: true,
  is_email_verified: false,
  password: "",
  is_password_temporary: true,
  is_admin: false,
  clients: [],
  locale: "en"
};

const ViewUpdateUser = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [user, setUser] = useState<UserCreate>(DEFAULT_USER);
  const [userNotFound, setUserNotFound] = useState(false);

  const queryClient = useQueryClient();
  const [userUpdating, setUserUpdating] = useState(false);

  const mutationPatchUser = usePatchUser();

  const { id: userId } = useParams<{ id: string }>();

  const { data, isFetching: isLoading, isError } = useUser(userId || "");

  const onGoBack = () => {
    navigate(ROUTES.ADMIN_PANEL + ROUTING_ADMIN_USERS);
  };

  const handleFieldChange = (field: keyof UserCreate, value: any) => {
    const value_fmt = `${value}`.length > 0 ? value : "";
    const updated = { ...user!!, [field]: value_fmt };
    setUser(updated);
  };

  const handleBoolFieldChanged = (field: T_BOOL_FIELD, value: boolean) => {
    const updated = { ...user!!, [field]: value };
    if (field === "is_admin") {
      updated.clients = [];
    }
    setUser(updated);
  };

  const handleClientsChange = (event: SelectChangeEvent) => {
    const {
      target: { value }
    } = event;

    const selectedClients =
      typeof value === "string"
        ? (value.split(",") as string[])
        : (value as string[]);
    const selectedClientsIds = selectedClients.map((client) =>
      parseInt(client)
    );

    const updated = {
      ...user!!,
      clients: selectedClientsIds
    };
    setUser(updated);
  };

  const onUpdate = (e: any) => {
    e.preventDefault();
    if (data && data.uuid.length === 0) {
      return;
    }

    if (!e.target.checkValidity()) {
      setSnackbarAlert(setErrorAlert(t("newUser.validation.formInvalid")));
    } else {
      setUserUpdating(true);

      const { username, ...rest } = user;
      const updates: PatchUser = { ...rest };

      if (user.password.length === 0) {
        updates.is_password_temporary = undefined;
        updates.password = undefined;
      }

      mutationPatchUser.mutate(
        {
          user_id: data!!.uuid,
          updates
        },
        {
          onSuccess: () => {
            setSnackbarAlert(
              setSuccessAlert(
                t("newUser.updated_success", { name: user.username })
              )
            );
            queryClient.invalidateQueries({
              queryKey: ["users"]
            });
            queryClient.refetchQueries({
              queryKey: ["users"]
            });

            queryClient.invalidateQueries({
              queryKey: ["user", data!!.uuid]
            });
            queryClient.refetchQueries({
              queryKey: ["user", data!!.uuid]
            });

            setUserUpdating(false);
          },
          onError: () => {
            setSnackbarAlert(
              setErrorAlert(t("newUser.update_error", { name: user.username }))
            );
            setUserUpdating(false);
          }
        }
      );
    }
  };

  useEffect(() => {
    if (isError || (!isLoading && (data === undefined || data === null))) {
      setUserNotFound(true);
      setSnackbarAlert(setErrorAlert(t("newUser.notFound", { id: userId })));
    }
    if (data) {
      const { uuid, ...rest } = data;
      setUser({ ...rest, password: "", is_password_temporary: false });
    }
  }, [data, isLoading, isError]);

  return (
    <Box className="w-full p-4">
      <NavigationButtons disabled={false} onGoBack={onGoBack} />

      <Box className="flex justify-center w-full p-2 md:p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <EditIcon color="info" sx={{ fontSize: "2.0rem" }} />
            <Typography variant="h6">{t("common.editUser")}</Typography>
          </Box>

          <UserForm
            mode="update"
            user={user}
            onSave={onUpdate}
            onGoBack={onGoBack}
            processing={userUpdating}
            loadingUser={isLoading || userNotFound}
            handleFieldChange={handleFieldChange}
            handleBoolFieldChanged={handleBoolFieldChanged}
            handleClientsChange={handleClientsChange}
          />
        </Paper>
      </Box>
      <CustomSnackBar
        id={snackbarAlert.message}
        key={snackbarAlert.message}
        open={snackbarAlert.open}
        message={snackbarAlert.message}
        handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
        autoHideDuration={snackbarAlert.autoHideDuration}
        color={snackbarAlert.severity}
      />
    </Box>
  );
};

export default ViewUpdateUser;
